import axios from "axios"
import filesaver from "file-saver"
import { effectZipPath, TextureFileNames, TexturePaths } from "services/player/types"
import { Cover, GlassesRenderingOptions } from "types"
import uzip from "uzip"

export const zip = (files: Record<string, Uint8Array>) => {
  const { encode: zip } = uzip
  const archive = zip(files)
  return new Blob([archive], { type: "application/zip" })
}

export const unzip = async (archive: Blob): Promise<Record<string, Uint8Array>> => {
  const buffer = await archive.arrayBuffer()
  const files = uzip.parse(buffer)
  const filtered = Object.entries(files)
    .filter(([name]) => !name.includes("__MACOSX"))
    .filter(([_, data]) => data?.length)

  return Object.fromEntries(filtered) as Record<string, Uint8Array>
}

export const download = (files: Record<string, Uint8Array>, name: string) => {
  const { saveAs: save } = filesaver
  const archive = zip(files)
  save(archive, name)
}

export const blobToBase64 = async (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      resolve(reader?.result?.toString())
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export const convertGlassesZipToObject = async (zip) => {
  try {
    const archive = await fetch(zip).then((res) => res.blob())
    const files = await unzip(archive)
    const renderingOptions = files[TexturePaths.RENDERING_OPTIONS]
      ? JSON.parse(new TextDecoder().decode(files[TexturePaths.RENDERING_OPTIONS]))
      : null

    const coverLeft = await createGlassesFileDataFromUint8Array(
      files[TexturePaths.MAT_LEFT_BASE_COLOR],
      TextureFileNames.MAT_LEFT_BASE_COLOR
    )
    const coverFront = await createGlassesFileDataFromUint8Array(
      files[TexturePaths.MAT_FRONT_BASE_COLOR],
      TextureFileNames.MAT_FRONT_BASE_COLOR
    )
    const coverRight = await createGlassesFileDataFromUint8Array(
      files[TexturePaths.MAT_RIGHT_BASE_COLOR],
      TextureFileNames.MAT_RIGHT_BASE_COLOR
    )

    return { coverFront, coverLeft, coverRight, renderingOptions }
  } catch (e) {
    return {}
  }
}

export const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  let binary = ""
  const len = bytes.byteLength
  for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i])

  return btoa(binary)
}

export const createGlassesFileDataFromUint8Array = async (
  fileData: Uint8Array,
  fileName: string
): Promise<Cover> => {
  const fileBase64 = uint8ArrayToBase64(fileData)

  return {
    fileBase64: `data:image/png;base64,${fileBase64}`,
    lastModified: new Date().getTime(),
    name: fileName,
    size: fileData.length,
    type: "image/png",
  }
}
export const base64ToUint8Array = (base64: string): Uint8Array => {
  const base64WithoutPrefix = base64.split(",")[1] || base64

  const binaryString = atob(base64WithoutPrefix)
  const len = binaryString.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i)

  return bytes
}

export const getCorrectConfigJson = async (renderingOptions: GlassesRenderingOptions) => {
  const zipEffectRequest = await axios(effectZipPath, { responseType: "blob" })
  const files = await unzip(zipEffectRequest.data)

  const configJson = JSON.parse(new TextDecoder().decode(files[TexturePaths.CONFIG_JSON]))

  if (configJson && configJson["components"]["transformation"]) {
    const { x: scaleX, y: scaleY, z: scaleZ } = renderingOptions.scale
    const { x: positionX, y: positionY, z: positionZ } = renderingOptions.positionAccuracy
    configJson["components"]["transformation"] = {
      ...configJson["components"]["transformation"],
      scale: [+scaleX, +scaleY, +scaleZ],
      translation: [+positionX, +positionY, +positionZ],
    }
  }

  return configJson
}

export const createGlassesCustomZipEffect = async ({
  coverLeft,
  coverFront,
  coverRight,
  renderingOptions,
  configJson,
}: {
  coverLeft: Cover
  coverFront: Cover
  coverRight: Cover
  renderingOptions: GlassesRenderingOptions
  configJson?: string
}) => {
  const zipEffectRequest = await axios(effectZipPath, { responseType: "blob" })
  const effect = await unzip(zipEffectRequest.data)

  if (coverLeft && coverLeft.fileBase64)
    effect[TexturePaths.MAT_LEFT_BASE_COLOR] = base64ToUint8Array(coverLeft.fileBase64)

  if (coverFront && coverFront.fileBase64)
    effect[TexturePaths.MAT_FRONT_BASE_COLOR] = base64ToUint8Array(coverFront.fileBase64)

  if (coverRight && coverRight.fileBase64)
    effect[TexturePaths.MAT_RIGHT_BASE_COLOR] = base64ToUint8Array(coverRight.fileBase64)

  if (renderingOptions)
    effect[TexturePaths.RENDERING_OPTIONS] = new TextEncoder().encode(
      JSON.stringify(renderingOptions)
    )

  if (configJson)
    effect[TexturePaths.CONFIG_JSON] = new TextEncoder().encode(JSON.stringify(configJson))

  return zip(effect)
}
